import { useState } from 'react'
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import { AdminSidebar } from '@/components/admin/AdminSidebar'
import { BreadcrumbContext, type BreadcrumbExtra } from '@/lib/breadcrumbContext'
import { useUser } from '@/contexts/UserContext'

type BreadcrumbEntry = { label: string; section?: string }
const BREADCRUMB_MAP: Record<string, BreadcrumbEntry> = {
  '/admin': { label: 'Visão geral' },
  '/admin/areas': { label: 'Árvore de áreas' },
  '/admin/types': { label: 'Tipos de arquivo' },
  '/admin/files': { label: 'Arquivos' },
  '/admin/trash': { label: 'Lixeira' },
  '/admin/users': { label: 'Usuários', section: 'Pessoas' },
  '/admin/profiles': { label: 'Perfis e permissões', section: 'Pessoas' },
  '/admin/audit': { label: 'Auditoria', section: 'Plataforma' },
  '/admin/settings': { label: 'Configurações', section: 'Plataforma' },
}

const SEP = <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, margin: '0 2px' }}>›</span>

export default function AdminLayout() {
  const { pathname } = useLocation()
  const { user, loading } = useUser()
  const [extra, setExtra] = useState<BreadcrumbExtra>(null)

  // Aguarda o load do user; sem nenhuma claim de admin (só OwnProfile.*) → redireciona pra home browse.
  if (loading) return null
  const hasAdminAccess = user?.permissions.some((p) => !p.startsWith('OwnProfile.')) ?? false
  if (!hasAdminAccess) return <Navigate to="/" replace />

  const entry = Object.entries(BREADCRUMB_MAP).find(
    ([k]) => pathname === k || (k !== '/admin' && pathname.startsWith(k))
  )
  const pageEntry = entry?.[1]
  const pageKey = entry?.[0]

  return (
    <BreadcrumbContext.Provider value={{ extra, setExtra }}>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', background: 'var(--ev-bg)' }}>
        <div style={{
          height: 56, flexShrink: 0,
          background: '#1A1F24', borderBottom: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', alignItems: 'stretch',
        }}>
          <div style={{
            width: 240, flexShrink: 0,
            display: 'flex', alignItems: 'center',
            padding: '0 18px',
            borderRight: '1px solid rgba(255,255,255,0.08)',
          }}>
            <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
              <img
                src="/assets/banesprev-logo.png"
                alt="Banesprev"
                style={{ height: 22, width: 'auto' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
              />
              <span style={{
                fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.6)',
                borderLeft: '1px solid rgba(255,255,255,0.2)', paddingLeft: 10,
              }}>
                GED
              </span>
            </Link>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 16, minWidth: 0 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
              background: 'rgba(159,194,224,0.18)', color: '#9FC2E0',
              borderRadius: 4, padding: '2px 7px',
              border: '1px solid rgba(159,194,224,0.35)',
              flexShrink: 0,
            }}>
              ADMIN
            </span>
            {pageEntry && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                <span>Admin</span>
                {pageEntry.section && <>{SEP}<span>{pageEntry.section}</span></>}
                {SEP}
                {extra ? (
                  <Link to={pageKey!} style={{ color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.9)')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.6)')}
                  >
                    {pageEntry.label}
                  </Link>
                ) : (
                  <span style={{ color: 'rgba(255,255,255,0.92)', fontWeight: 500 }}>{pageEntry.label}</span>
                )}
                {extra && (
                  <>{SEP}<span style={{ color: 'rgba(255,255,255,0.92)', fontWeight: 500 }}>{extra.label}</span></>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <AdminSidebar />
          <main style={{ flex: 1, overflow: 'auto', background: 'var(--ev-bg-alt)' }}>
            <Outlet />
          </main>
        </div>
      </div>
    </BreadcrumbContext.Provider>
  )
}
